import React, { useState, useEffect, useRef } from "react";
import { Nav } from "react-bootstrap";
import logo from "../img/logo.png";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  FaTachometerAlt,
  FaBuilding,
  FaUsersCog,
  FaTicketAlt,
  FaChartBar,
  FaStore,
  FaUser,
  FaUserTie,
  FaTags,
} from "react-icons/fa";
import AppNavbar from "./common/AppNavbar";
import { decryptText } from "../utility/cryptoUtils";

const DashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const sidebarRef = useRef(null);

  const role = decryptText(localStorage.getItem("role"))?.toLowerCase();
  const email = decryptText(localStorage.getItem("email"))?.toLowerCase();

  // 🔓 New: dynamic allowed modules from localStorage
  const allowedModule = JSON.parse(decryptText(localStorage.getItem("allowedModule")) || "[]")
    .map((m) => m.toLowerCase());

  const isAdmin = role === "admin";

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    navigate("/");
  };

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        window.innerWidth < 992 &&
        sidebarOpen
      ) {
        setSidebarOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [sidebarOpen]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth >= 992) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isActive = (path) => location.pathname.startsWith(path);

  // 🧭 Sidebar configuration (no UI change)
  const allLinks = [
    { to: "/dashboard", icon: <FaTachometerAlt style={{ color: "#F44336" }} />, label: "Dashboard", key: "dashboard" },
    { to: "/department", icon: <FaBuilding style={{ color: "#8BC34A" }} />, label: "Departments", key: "departments" },
    { to: "/staff", icon: <FaUsersCog style={{ color: "#FFC107" }} />, label: "Staff Management", key: "Staff Management" },
    { to: "/user", icon: <FaUser style={{ color: "#4CAF50" }} />, label: "User", key: "user" },
    { to: "/client", icon: <FaUserTie style={{ color: "#c063ff" }} />, label: "Clients", key: "clients" },
    { to: "/vendor", icon: <FaStore style={{ color: "#f26471" }} />, label: "Vendors", key: "vendors" },
    { to: "/category", icon: <FaTags style={{ color: "#009688" }} />, label: "Category", key: "category" },
    { to: "/tickets", icon: <FaTicketAlt style={{ color: "#FFEB3B" }} />, label: "Tickets", key: "tickets" },
    { to: "/reports", icon: <FaChartBar style={{ color: "#00BCD4" }} />, label: "Reports", key: "reports" },
  ];

  const canAccess = (key) => allowedModule.includes(key.toLowerCase());

  return (
    <div className="d-flex bg-dark position-relative" style={{ minHeight: "100vh" }}>
      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className={`custom-sidebar bg-dark text-white p-2 shadow-sm ${
          sidebarOpen ? "open" : "closed"
        } ${windowWidth < 992 ? "position-fixed h-100 z-3" : "position-fixed h-100vh"}`}
        style={{
          width: "250px",
          minWidth: "250px",
          transition: "transform 0.3s ease",
          transform: sidebarOpen ? "translateX(0)" : "translateX(-100%)",
          borderRight: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <div className="sticky-top bg-dark pt-3 pb-3 border-bottom border-secondary">
          <div className="text-center">
            <img src={logo} alt="Company Logo" className="img-fluid" style={{ maxHeight: "50px" }} />
          </div>
        </div>

        <div
          className="sidebar-scrollable-content"
          style={{
            height: "calc(100vh - 80px)",
            paddingTop: "10px",
            paddingBottom: "20px",
          }}
        >
          <Nav className="flex-column gap-2">
            {allLinks.map(({ to, icon, label, key }) => {
              const allowed = canAccess(key);
              if (allowed) {
                return (
                  <Link
                    key={to}
                    className={`nav-link text-white d-flex align-items-center sidebar-link ${
                      isActive(to) ? "active bg-secondary" : "hover-bg-dark"
                    }`}
                    to={to}
                    onClick={() => windowWidth < 992 && setSidebarOpen(false)}
                  >
                    <span className="me-3 fs-4">{icon}</span>
                    <span>{label}</span>
                  </Link>
                );
              } else {
                return (
                  <div
                    key={to}
                    className="nav-link d-flex align-items-center sidebar-link text-white-50"
                    style={{ cursor: "not-allowed", opacity: 0.5 }}
                  >
                    <span className="me-3 fs-4">{icon}</span>
                    <span>{label}</span>
                  </div>
                );
              }
            })}
          </Nav>
        </div>
      </div>

      {/* Main Content */}
      <div
        className="flex-grow-1 bg-dark text-white"
        style={{
          minHeight: "100vh",
          paddingLeft: windowWidth > 992 ? "235px" : "0px",
          width: sidebarOpen && windowWidth >= 992 ? "calc(100% - 100px)" : "100%",
          transition: "margin-left 0.3s ease",
        }}
      >
        <AppNavbar handleLogout={handleLogout} toggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />

        <div className="px-3 px-md-4 pt-3 pb-5" style={{ minHeight: "calc(100vh - 60px)" }}>
          <Outlet />
        </div>

        <footer className="bg-dark py-3 border-top border-secondary">
          <div className="container-fluid text-center">
            <p className="m-0">
              © {new Date().getFullYear()}, Hogist Technologies Private Limited CRM. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default DashboardLayout;
